import Table from '@components/General/Table';
import { TableCell } from '@mui/material';
import styles from '@styles/screens/PresenteeismTableControlBoard.module.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Image from '@components/General/Image';
import { BuildingPickerFetchPopper, DatePickerPopperWithRange, DropdownListPickerPopper } from '@components/General/TablePoppers';
import { Navigate } from 'react-router-dom';

import { format, utcToZonedTime } from 'date-fns-tz';

import { FichadaManualModal } from '@components/Listado/FichadaManualModal';
import { usePopup } from '@contexts/popupContext';
import * as XLSX from 'xlsx'
import { Application } from '@core/Application';
import { AnalyticsHelper } from '@core/Analytics/AnalyticsHelper';
import { useAuth } from '@contexts/authContext';
import { Description } from '@utils/DescriptionsHelper';
import { render } from 'react-dom';
import { Logger } from '@utils/Logger';
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { Text } from '@shared/designSystem/Texts';
import { Modal } from '@shared/designSystem/messages/Modal';
import { PresenteeismConciliationDetailService } from '@services/PresenteeismConciliationDetailService';
import PresenteeismConciliationDetailModal from './PresenteeismConciliationDetailModal';
import { PresenteeismConciliationDetailRowRequest } from './PresenteeismConciliationDetailModalViewModel';
import PresenteeismManualCheckoutModal from './PresenteeismManualCheckoutModal';
import KioskEvidenceModal from './KioskEvidenceModal';
import { PendingCheckoutRowRequest } from './PresenteeismManualCheckoutModalViewModel';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import { QontyLoading } from '@components/QontyLoading';
import { QontyEmptyState } from '@components/QontyEmptyState';
import SearchBar from '@shared/designSystem/SearchBar';
import { DateTriggerButton, HollowedButton, RefreshButton } from '@shared/designSystem/Button';
import { ClickableWrapper } from '@shared/designSystem/ClickableWrapper';

// Popup de detalle de fichada manual
const ManualRecordPopup = ({ data, hidePopup, timezone, collaboratorName, recordType }) => {
    const responsible = (data?.createdByName || 'Sin información'.localized()).trim();
    const collaborator = (collaboratorName || 'Sin información'.localized()).trim();
    const typeLabel = recordType || 'Sin información'.localized();
    const date = data?.timestamp
        ? Description.Timestamp.fullDate(data.timestamp, timezone)
        : 'Sin información'.localized();

    return (
        <Modal
            isOpen={true}
            onClose={hidePopup}
            title={"Fichada Manual".localized()}
            maxWidth="520px"
        >
            <VerticalContainer gap={designSystemStyles.gapLg} alignment="left">
                <VerticalContainer gap={designSystemStyles.gapLg} alignment="left">
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Colaborador".localized()} />
                        <Text text={`**${collaborator}**`} markdown={true} />
                    </HorizontalContainer>
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Tipo".localized()} />
                        <Text text={`**${typeLabel}**`} markdown={true} />
                    </HorizontalContainer>
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Responsable".localized()} />
                        <Text text={`**${responsible}**`} markdown={true} />
                    </HorizontalContainer>
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Fecha y Hora".localized()} />
                        <Text text={`**${date}**`} markdown={true} />
                    </HorizontalContainer>
                </VerticalContainer>
                <HorizontalContainer justifyContent={designSystemStyles.justifyContentRight} width="100%">
                    <HollowedButton onClick={hidePopup} text={"Cerrar".localized()} />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

const ABConciliationPopup = ({ data, hidePopup }) => {
    const checkinBuilding = data?.abConciliationInfo?.checkinBuilding;
    const collaborator = (data?.colaborador || 'Sin información'.localized()).trim();
    const checkinBuildingName = (
        checkinBuilding?.name ||
        checkinBuilding?.buildingName ||
        checkinBuilding?.title ||
        'Sin información'.localized()
    ).trim();

    const checkoutBuilding = data?.abConciliationInfo?.checkoutBuilding
    const checkoutBuildingName = (
        checkoutBuilding?.name ||
        checkoutBuilding?.buildingName ||
        checkoutBuilding?.title ||
        'Sin información'.localized()
    ).trim();

    return (
        <Modal
            isOpen={true}
            onClose={hidePopup}
            title={"Conciliación AB".localized()}
            maxWidth="520px"
        >
            <VerticalContainer gap={designSystemStyles.gapLg} alignment="left">
                <VerticalContainer gap={designSystemStyles.gapLg} alignment="left">
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Colaborador".localized()} />
                        <Text text={`**${collaborator}**`} markdown={true} />
                    </HorizontalContainer>
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Entrada".localized()}/>
                        <HorizontalContainer justifyContent={designSystemStyles.justifyContentRight}>
                            <Text text={data.ingreso}/>
                            <Text text={"**"+checkinBuildingName+"**"} markdown={true}/>
                        </HorizontalContainer>
                    </HorizontalContainer>
                    <HorizontalContainer justifyContent="space-between" width="100%">
                        <Text text={"Salida".localized()} />
                        <HorizontalContainer justifyContent={designSystemStyles.justifyContentRight}>
                            <Text text={data.egreso}/>
                            <Text text={"**"+checkoutBuildingName+"**"} markdown={true}/>
                        </HorizontalContainer>
                    </HorizontalContainer>
                </VerticalContainer>
                <HorizontalContainer justifyContent={designSystemStyles.justifyContentRight} width="100%">
                    <HollowedButton onClick={hidePopup} text={"Cerrar".localized()} />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

interface DropdownFilterItem {
    id: string;
    name: string;
}

/** Marks a fichada taken at a face-recognition kiosk, which has photos to show. */
const FaceBadgeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.3" />
        <path d="M2.75 14c0-2.6 2.35-4.25 5.25-4.25S13.25 11.4 13.25 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
);

/** A kiosk fichada whose photo the anti-spoof check flagged as a possible impersonation. */
const SpoofWarningIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.75 14.75 13.75H1.25L8 1.75Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M8 6.25v3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="8" cy="11.6" r="0.8" fill="currentColor" />
    </svg>
);

const DotIcon = ({ color }) => {
    return (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
        <circle cx="4" cy="4" r="3" fill={color}/>
    </svg>
    )
}


const makeRowTemplate = (showPopup, hidePopup, timezone, onOpenConciliationDetail, onOpenPendingCheckout, canManualRecord) => (_index, row) => {

    const renderManualBadge = (data, collaboratorName, recordType) => {
        if (!data) return null;
        return (
            <div 
                className={styles['manual-badge']}
                onClick={() => showPopup(() => (
                    <ManualRecordPopup
                        data={data}
                        hidePopup={hidePopup}
                        timezone={timezone}
                        collaboratorName={collaboratorName}
                        recordType={recordType}
                    />
                ))}
            >
                M
            </div>
        );
    };

    /**
     * Kiosk fichadas carry a face photo. The flag comes precomputed from the table node
     * (the record's origin, which never changes) rather than from the presence of the
     * photo, which lands seconds after the record and would still be missing when the
     * node is built. The modal is what discovers whether the images actually resolve.
     *
     * A fichada the anti-spoof check flagged gets a warning variant of the same badge;
     * that flag is also precomputed on the node, and only for organizations with the
     * anti-spoof alerts enabled.
     */
    const renderKioskEvidenceBadge = (recordID, isFromKiosk, collaboratorName, recordTypeLabel, timeLabel, isSpoofSuspected = false) => {
        if (!isFromKiosk || !recordID) return null;
        return (
            <div
                className={isSpoofSuspected ? `${styles['kiosk-evidence-badge']} ${styles['kiosk-evidence-badge-spoof']}` : styles['kiosk-evidence-badge']}
                title={isSpoofSuspected ? "Posible suplantación: ver fotos de la fichada".localized() : "Ver fotos de la fichada".localized()}
                onClick={() => showPopup(() => (
                    <KioskEvidenceModal
                        request={{
                            recordID,
                            collaboratorName: collaboratorName || '',
                            recordTypeLabel,
                            timeLabel: timeLabel || '',
                            spoofSuspected: isSpoofSuspected
                        }}
                        hidePopup={hidePopup}
                    />
                ))}
            >
                {isSpoofSuspected ? <SpoofWarningIcon /> : <FaceBadgeIcon />}
            </div>
        );
    };

    const renderABConciliationBadge = (data) => {
        if (!data.abConciliationInfo) return null;
        return (
            <div 
                onClick={() => showPopup(() => <ABConciliationPopup data={data} hidePopup={hidePopup} />)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    borderRadius: designSystemStyles.cornerRadius,
                    background: designSystemStyles.colorBackgroundWhite,
                    border: `1px solid ${designSystemStyles.colorMainAction}`,
                    color: designSystemStyles.colorMainAction,
                    fontFamily: designSystemStyles.fontFamily,
                    fontSize: designSystemStyles.fontSizeXsm,
                    fontWeight: designSystemStyles.fontWeightMainAction,
                    cursor: 'pointer',
                    transition: designSystemStyles.transitionEase,
                }}
            >
                AB
            </div>
        );
    };

    const getColor = (color) => {
        switch(color) {
            case 'green':
                return '#56A725'
            case 'red':
                return '#FF5454'
            case 'yellow':
                return '#EEA821'
            default: 
                return '#6C778B'
        }
    }

    const processName = (name) => {
        const words = name.split(' ');
        const processedWords = words.map(word => {
            const firstLetter = word.charAt(0).toUpperCase();
            const restOfWord = word.slice(1).toLowerCase();
            return firstLetter + restOfWord;
        });
        return processedWords.join(' ');        
    }

    const canOpenConciliationDetail = Boolean(
        row.conciliationDetailRequest?.organizationID
        && row.conciliationDetailRequest?.requesterID
        && row.conciliationDetailRequest?.day
        && row.conciliationDetailRequest?.tableRecordKey
    );

    const openConciliationDetail = () => {
        if (canOpenConciliationDetail) {
            AnalyticsHelper.trackPresenteeismConciliationDetailShown(
                row.conciliationDetailRequest.requesterID,
                row.conciliationDetailRequest.tableRecordKey,
                row.conciliationDetailRequest.day,
                row.conciliationDetailRequest.buildingID,
                row.conciliationDetailRequest.checkInTimestamp
            );
            onOpenConciliationDetail(row.conciliationDetailRequest);
        }
    };

    const handleAnalysisKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openConciliationDetail();
        }
    };

    const canOpenPendingCheckout = Boolean(
        canManualRecord
        && row.egreso === 'Pendiente'
        && !row.isCanceled
        && row.pendingCheckoutRequest?.organizationID
        && row.pendingCheckoutRequest?.requesterID
        && row.pendingCheckoutRequest?.day
        && row.pendingCheckoutRequest?.tableRecordKey
    );

    const openPendingCheckout = () => {
        if (canOpenPendingCheckout) {
            onOpenPendingCheckout(row.pendingCheckoutRequest);
        }
    };

    const isCanceled = row.isCanceled === true;
    const canceledTextColor = "#98A2B3";
    const tableCellStyle = isCanceled ? { color: canceledTextColor } : undefined;
    const analysisDotColor = isCanceled ? canceledTextColor : getColor(row.analisis.color);

    return (
        <React.Fragment>
            <TableCell component="th" scope="row" align='left' style={tableCellStyle}>
                <div className={styles['circle-mask']}  style={{width: '24px', height: '24px', marginRight: '10px', display: 'inline-block', verticalAlign: 'top', opacity: isCanceled ? 0.45 : 1}}>
                    <Image src={row.photo || '/assets/Q.png'} alt="Profile Picture" isRound={true} style={{width: '100%', height: '100%'}}/>
                </div>
                <div style={{display: 'inline-block', maxWidth: 'calc(100% - 34px)', color: isCanceled ? canceledTextColor : (row.deactivated ? "gray" : '#101828'), fontWeight: 'bold'}}>

                    {processName(row.colaborador)}
                </div>
            </TableCell>
            <TableCell align="center" className={styles['table-row']} style={tableCellStyle}>{row.fecha}</TableCell>
            <TableCell align="center" className={styles['table-row']} style={tableCellStyle}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'}}>
                    <span>{row.ingreso}</span>
                    {renderManualBadge(row.checkinAuditInfo, row.colaborador, "Ingreso")}
                    {renderKioskEvidenceBadge(row.checkinRecordID, row.isCheckinFromKiosk, row.colaborador, "Ingreso", row.ingreso, row.isCheckinSpoofSuspected)}
                </div>
            </TableCell>
            <TableCell align="center" className={styles['table-row']} style={tableCellStyle}>
                {canOpenPendingCheckout ? (
                    <ClickableWrapper
                        onClick={openPendingCheckout}
                        title={"Fichar salida".localized()}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            textDecoration: 'underline',
                        }}
                    >
                        <span>{row.egreso}</span>
                        {renderManualBadge(row.checkoutAuditInfo, row.colaborador, "Egreso")}
                        {renderKioskEvidenceBadge(row.checkOutRecordID, row.isCheckoutFromKiosk, row.colaborador, "Egreso", row.egreso, row.isCheckoutSpoofSuspected)}
                    </ClickableWrapper>
                ) : (
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'}}>
                        <span>{row.egreso}</span>
                        {renderManualBadge(row.checkoutAuditInfo, row.colaborador, "Egreso")}
                        {renderKioskEvidenceBadge(row.checkOutRecordID, row.isCheckoutFromKiosk, row.colaborador, "Egreso", row.egreso, row.isCheckoutSpoofSuspected)}
                    </div>
                )}
            </TableCell>
            <TableCell align="left" className={styles['table-row']} style={tableCellStyle}>
                <div
                    role={canOpenConciliationDetail ? "button" : undefined}
                    tabIndex={canOpenConciliationDetail ? 0 : undefined}
                    onClick={openConciliationDetail}
                    onKeyDown={handleAnalysisKeyDown}
                    style={{
                        display: 'flex',
                        paddingLeft: '20px',
                        flexDirection: 'column',
                        cursor: canOpenConciliationDetail ? 'pointer' : 'default',
                        outline: 'none',
                    }}
                    aria-label={canOpenConciliationDetail ? "Ver detalle de conciliación".localized() : undefined}
                >

                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <circle cx="4" cy="4" r="3" fill={analysisDotColor}/>
                        </svg>
                        <div className={styles['analisis-text']} style={{paddingLeft: '5px', color: isCanceled ? canceledTextColor : undefined}}>
                            {row.analisis.hsTrabajadasText}
                        </div>
                    </div>
                    <div style={{display: 'flex'}}>
                        <div style={{width: '8px', height: '8px', color: isCanceled ? canceledTextColor : '#888693'}}>
                            {row.analisis.sign}
                        </div>
                        <div className={styles['shift-text']} style={{paddingLeft: '5px', color: isCanceled ? canceledTextColor : undefined}}>
                            {row.analisis.subtext}
                        </div>
                    </div>

                </div>
            </TableCell>
            <TableCell align="center" className={styles['table-row']} style={tableCellStyle}>
                <HorizontalContainer 
                    gap={designSystemStyles.gapSm} 
                    justifyContent="center" 
                >
                    {row.edificio}
                    {renderABConciliationBadge(row)}
                </HorizontalContainer>
            </TableCell>
            <TableCell/>
        </React.Fragment>
    );
}

function minutesToHours(minutes) {
    // Round to the nearest whole number to avoid floating-point arithmetic issues
    const roundedMinutes = Math.round(minutes);

    // Calculate the number of hours and the remaining minutes
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    // Format the hours and minutes to ensure two digits
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = remainingMinutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
}


interface ListadoRow {
    numeroWhatsapp: string
    colaborador: string
    photo: string
    fecha: string
    ingreso: string
    egreso: string
    analisis: {
        hsTrabajadasText: string
        subtext: string
        color: string
        sign: string
        xlsExtraHours: string
        xlsTotalHours: string
        isCheckoutDayOverriden: boolean
    }
    edificio:string
    /**
     * The building this row belongs to. `edificio` carries the NAME, and the name
     * on a row is a snapshot the worker froze the day the record was written and
     * never updates, so a renamed QontactPoint splits its own history in two.
     * Filtering has to happen on this ID, which does not change; the name is only
     * for display.
     */
    edificioID?:string
    isCanceled?: boolean
    conciliationDetailRequest?: PresenteeismConciliationDetailRowRequest
    pendingCheckoutRequest?: PendingCheckoutRowRequest
}


const PresenteeismTableControlBoard = ({org}) => {

    const [orgTimezone, setOrgTimezone] = useState(org.timezone ? org.timezone : 'America/Argentina/Buenos_Aires');

    const [open, setOpen] = useState(false)
    const datePickerAnchorRef = useRef<HTMLDivElement | null>(null)
    const [anchorEl, setAnchorEl] = useState(null)

    const [loading, setLoading] = useState(false)
    const [tableError, setTableError] = useState<string | null>(null)
    const [response, setResponse] = useState([])
    const [searchField, setSearchField] = useState('')

    const [buildingsToFilter, setBuildingsToFilter] = useState([])

    const [date, setDate] = useState(new Date())
    const [dateRange, setDateRange] = useState(null)
    const [inputDateMethod, setInputDateMethod] = useState('Unica')

    const [analisisFilter, setAnalisisFilter] = useState<DropdownFilterItem>({id: 'all', name: 'Todos'})
    const [egresosFilter, setEgresosFilter] = useState<DropdownFilterItem>({id: 'all', name: 'Todos'})
    const [conciliationDetailRequest, setConciliationDetailRequest] = useState<PresenteeismConciliationDetailRowRequest | null>(null)
    const [pendingCheckoutRequest, setPendingCheckoutRequest] = useState<PendingCheckoutRowRequest | null>(null)

    const { showPopup, hidePopup } = usePopup();
    const { userRole } = useAuth();
    const canManualRecord = Boolean(userRole?.permissions.some(permission => permission.id === "COLLABORATORS_MANUAL_RECORD"));
    const conciliationDetailService = useMemo(() => (
        new PresenteeismConciliationDetailService(Application.shared().coreDependencies.httpClient)
    ), []);
    const dashboardService = Application.shared().coreDependencies.dashboardService;

    const columns = useMemo(() => [
        {
          label: 'Colaborador',
          dataKey: 'colaborador',
        },
        {
          label: 'Fecha',
          dataKey: 'fecha',
          size: '12.5%',
        },
        {
          label: 'Ingreso',
          dataKey: 'ingreso',
          size: '10%',
        },
        {
          label: 'Egreso',
          dataKey: 'egreso',
          component: DropdownListPickerPopper,
          componentProps: {
            placement: 'bottom-end',
            items: [
                {id: 'all', name: "Todos"},
                {id: 'pending', name: "Pendientes"},
            ],
            selectedItem: egresosFilter,
            setSelectedItem: setEgresosFilter
          },
          size: '10%',
        },
        {
          label: 'Análisis',
          dataKey: 'analisis',          
          component: DropdownListPickerPopper,
          componentProps: {
            placement: 'bottom-end',
            items: [
                {id: 'all', name: "Todos"},
                {id: 'green', name: "Llegada a tiempo", icon: <DotIcon color="#56A725"/>},
                {id: 'red', name: "Llegada tarde", icon: <DotIcon color="#FF5454"/>},
                {id: 'gray', name: "Sin turno asignado", icon: <DotIcon color="#6C778B"/>},
            ],
            selectedItem: analisisFilter,
            setSelectedItem: setAnalisisFilter,
            shouldClickAwayOnChoose: true
          },
        },
        {
          label: 'Edificio',
          dataKey: 'edificio',
          component: BuildingPickerFetchPopper,
          componentProps: {
            placement: 'bottom-end',
            orgID: org.id,
            initBuildingsToFilter: buildingsToFilter,
            setBuildingsToFilter: setBuildingsToFilter,
          },
          size: '18%',
        },
        {
          label: '',
          dataKey: 'info',
          size: '10px'
        }
    ], [buildingsToFilter, analisisFilter, egresosFilter]);

    const fetchPresenteeismResponse = useCallback(async (isRange) => {
        setLoading(true)
        setTableError(null)
        setResponse([])
        try {

            const dashboardService = Application.shared().coreDependencies.dashboardService;
            let result: any
            if (isRange){
                let startDate = dateRange[0].toLocaleDateString('en-ZA', {timeZone: orgTimezone}).replace(/\//g, '-')
                let endDate = dateRange[1].toLocaleDateString('en-ZA', {timeZone: orgTimezone}).replace(/\//g, '-')
                result = await dashboardService.getPresenteeismTableRange(org.id, startDate, endDate)
            } else {
                let processedDate = date.toLocaleDateString('en-ZA', {timeZone: orgTimezone}).replace(/\//g, '-')
                result = await dashboardService.getPresenteeismTableDay(org.id, processedDate)
            }

            let data: any
            if (!isRange) {
                data = transformData(result)
            } else {
                data = transformRangeData(result)
            }

            setResponse(data)
        } catch (error) {
            console.error(error)
            setResponse([])
            setTableError("No se pudo cargar el listado de presentismo.".localized())
        } finally {
            setLoading(false)
        }
    }, [date, dateRange, org.id, orgTimezone])

    // El modal de fichada manual vive en el contexto de popups, así que su callback
    // sobrevive a varios renders de esta pantalla. Se refresca a través de un ref para
    // que siempre vuelva a pedir el rango/fecha vigente y no el que estaba seleccionado
    // cuando se abrió el modal.
    const refreshPresenteeismRef = useRef<() => void>(() => {})
    useEffect(() => {
        refreshPresenteeismRef.current = () => fetchPresenteeismResponse(dateRange ? true : false)
    }, [fetchPresenteeismResponse, dateRange])

    const refreshPresenteeism = useCallback(() => {
        refreshPresenteeismRef.current()
    }, [])

    //Effects
    useEffect(() => {
        console.log(org)
        if (org.timezone) {
            setOrgTimezone(org.timezone)
        }
        // fetchQPsResponse()
        // fetchNominaResponse()
    }, [org])

    useEffect(() => {
        if (date) {
            fetchPresenteeismResponse(false)
            AnalyticsHelper.trackFichadasSingleDate(
                date?.toLocaleDateString('en-ZA', {timeZone: orgTimezone}).replace(/\//g, '-')
            )
        } else {
            setTableError(null)
            setResponse([])
        }
    }, [date, fetchPresenteeismResponse, orgTimezone])

    useEffect(() => {
        if (dateRange) {
            fetchPresenteeismResponse(true)
            AnalyticsHelper.trackFichadasRange(
                dateRange[0]?.toLocaleDateString('en-ZA', {timeZone: orgTimezone})?.replace(/\//g, '-'),
                dateRange[1]?.toLocaleDateString('en-ZA', {timeZone: orgTimezone})?.replace(/\//g, '-')
            )
        } else {
            setTableError(null)
            setResponse([])
        }
    }, [dateRange, fetchPresenteeismResponse, orgTimezone])

    //State change functions
    const updateDate = (updatedDate) => {
        setDate(updatedDate)
    }

    const updateDateRange = (date1, date2) => {
        let startDate = date1 < date2 ? date1 : date2;
        let endDate = date1 > date2 ? date1 : date2;
    
        let processedStartDate, processedEndDate;
    
        if (startDate && endDate) {
            processedStartDate = startDate
            processedEndDate = endDate
            setDateRange([processedStartDate, processedEndDate])
        } else {
            setDateRange(null)
        }
    }
 
    //Fetches

    //Data transformation
    function buildBaseDetailRequest({
        record,
        requesterID,
        tableRecordKey,
        buildingID,
        collaboratorName,
    }): PresenteeismConciliationDetailRowRequest | undefined {
        const checkInTimestamp = Number(record?.checkInTimestamp ?? record?.timestamp);
        const hasCheckInTimestamp = Number.isFinite(checkInTimestamp);

        const day = record?.day || (
            hasCheckInTimestamp
                ? new Date(checkInTimestamp).toLocaleDateString('en-ZA', {timeZone: orgTimezone}).replace(/\//g, '-')
                : undefined
        );
        const resolvedTableRecordKey = tableRecordKey
            || record?.ticketID
            || record?.checkinTicketID
            || record?.checkInTicketID
            || record?.firestoreRecordId
            || record?.firestoreRecordID
            || record?.checkinRecordID
            || record?.checkInRecordID
            || (hasCheckInTimestamp ? `timestamp-${checkInTimestamp}` : undefined);

        if (!requesterID || !day || !resolvedTableRecordKey) {
            return undefined;
        }

        return {
            organizationID: org.id,
            requesterID,
            day,
            tableRecordKey: resolvedTableRecordKey,
            buildingID,
            checkInTimestamp: hasCheckInTimestamp ? checkInTimestamp : undefined,
            collaboratorName,
        };
    }

    function buildConciliationDetailRequest({
        record,
        requesterID,
        tableRecordKey,
        buildingID,
        collaboratorName,
    }): PresenteeismConciliationDetailRowRequest | undefined {
        const checkOutTimestamp = Number(record?.checkOutTimestamp ?? record?.conciliationInfo?.checkoutTimestamp);
        const hasCheckoutReference = Boolean(
            record?.conciliationInfo?.checkoutRecordID
            || record?.checkoutRecordID
            || record?.checkOutRecordID
            || record?.conciliationInfo?.checkoutTicketID
            || record?.checkoutTicketID
            || record?.checkOutTicketID
            || record?.checkOutTimestamp
        );
        const hasCompletedConciliation = Number.isFinite(checkOutTimestamp)
            && checkOutTimestamp > 0
            && hasCheckoutReference;

        if (!hasCompletedConciliation) {
            return undefined;
        }

        return buildBaseDetailRequest({ record, requesterID, tableRecordKey, buildingID, collaboratorName });
    }

    function buildPendingCheckoutRequest({
        record,
        requesterID,
        tableRecordKey,
        buildingID,
        collaboratorName,
        photo,
        edificioDisplay,
        fechaDisplay,
    }): PendingCheckoutRowRequest | undefined {
        const baseRequest = buildBaseDetailRequest({ record, requesterID, tableRecordKey, buildingID, collaboratorName });

        if (!baseRequest) {
            return undefined;
        }

        return {
            ...baseRequest,
            photo,
            edificioDisplay,
            fechaDisplay,
        };
    }

    function transformData(data) {
        let result = [];
        for (let buildingID in data.buildings) {
            for (let userID in data.buildings[buildingID].user_records) {
                let user = data.buildings[buildingID].user_records[userID];
                for (let recordID in data.buildings[buildingID].user_records[userID].records) {
                    let record = data.buildings[buildingID].user_records[userID].records[recordID];

                    let convertedRecord = transformRecord(record)

                    result.push({
                        numeroWhatsapp: userID,
                        colaborador: user.name,
                        photo: user.image,
                        ...convertedRecord,
                        edificio: data.buildings[buildingID].name,
                        edificioID: buildingID,
                        abConciliationInfo: convertedRecord.abConciliationInfo || null,
                        conciliationDetailRequest: buildConciliationDetailRequest({
                            record,
                            requesterID: userID,
                            tableRecordKey: recordID,
                            buildingID,
                            collaboratorName: user.name,
                        }),
                        pendingCheckoutRequest: buildPendingCheckoutRequest({
                            record,
                            requesterID: userID,
                            tableRecordKey: recordID,
                            buildingID,
                            collaboratorName: user.name,
                            photo: user.image,
                            edificioDisplay: data.buildings[buildingID].name,
                            fechaDisplay: convertedRecord.fecha,
                        }),
                    });
                }
            }
        } 
        return result;
    }

    function transformRangeData(data) {
        let result = [];

        for (let building in data.buildings) {
            for (let user_record in data.buildings[building].user_records) {
                let record = data.buildings[building].user_records[user_record];
                let convertedRecord = {}

                if (record.checkInTimestamp) {
                    convertedRecord = transformRecord(record)
                }

                result.push({
                    numeroWhatsapp: record.telefono,
                    colaborador: record.name,
                    photo: record.image,
                    ...convertedRecord,
                    edificio: data.buildings[building].name,
                    edificioID: building,
                    abConciliationInfo: record.abConcliationInfo || record.abConciliationInfo || null,
                    conciliationDetailRequest: buildConciliationDetailRequest({
                        record,
                        requesterID: record.requesterID || record.telefono || user_record,
                        tableRecordKey: record.ticketID || record.firestoreRecordId || record.firestoreRecordID || record.checkinRecordID || record.checkInRecordID,
                        buildingID: record.buildingID || building,
                        collaboratorName: record.name,
                    }),
                    pendingCheckoutRequest: buildPendingCheckoutRequest({
                        record,
                        requesterID: record.requesterID || record.telefono || user_record,
                        tableRecordKey: record.ticketID || record.firestoreRecordId || record.firestoreRecordID || record.checkinRecordID || record.checkInRecordID,
                        buildingID: record.buildingID || building,
                        collaboratorName: record.name,
                        photo: record.image,
                        edificioDisplay: data.buildings[building].name,
                        fechaDisplay: (convertedRecord as any).fecha,
                    }),
                });
            }
        }
        return result;
    }

    function transformRecord(record) {

        let dateCheckin = new Date(record.checkInTimestamp)

        let formattedDate = dateCheckin.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit', year: 'numeric', timeZone: orgTimezone})
        
        let zonedDate = utcToZonedTime(dateCheckin, orgTimezone)
        let ingreso = format(zonedDate, 'HH:mm', { timeZone: orgTimezone });
        let egreso = 'Pendiente'

        let analisisText = "No se registró salida aún"
        let subtext = "Sin información aún"
        let color = 'gray'
        let sign = " "

        let xlsTotalHours = "Sin información aún"
        let xlsExtraHours = "Sin información aún"
        const totalMinutesWorked = Number(record?.analisis?.totalMinutesWorked);
        const isCompletedRecord = Number(record?.checkOutTimestamp ?? 0) > 0;
        const isCanceledByZeroMinutes = isCompletedRecord && Number.isFinite(totalMinutesWorked) && totalMinutesWorked === 0;
        
        // Datos de fichadas manuales desde el backend
        let manualCheckIn = record.checkinAuditInfo;
        let manualCheckOut = record.checkoutAuditInfo;

        // Logger.logObject('PresenteeismTableControlBoard - transformRecord - record', record);

        if (record.checkOutTimestamp !== 0) {
            //Tiene check-out
            let dateCheckout = new Date(record.checkOutTimestamp)
            let zonedCheckout = utcToZonedTime(dateCheckout, orgTimezone)
            egreso = format(zonedCheckout, 'HH:mm', { timeZone: orgTimezone });
            analisisText = `${minutesToHours(record.analisis.totalMinutesWorked)} horas trabajadas`

            xlsTotalHours = minutesToHours(record.analisis.totalMinutesWorked)

            if (record.hasShift) {
                //Tiene check-out y turno asignado

                let expectedMinutes = record.analisis.expectedMinutesWorked
                let totalMinutes = record.analisis.totalMinutesWorked
                let difference = Math.round(totalMinutes - expectedMinutes)

                let minutesBetween = Math.abs(difference)
                xlsExtraHours = `${minutesToHours(minutesBetween)}`
                if (difference > 0) {
                    subtext = `${minutesToHours(minutesBetween)} horas extra`
                    sign = '+'
                } else if (difference < 0) {
                    subtext = `${minutesToHours(minutesBetween)} horas faltantes`
                    sign = '-'
                    xlsExtraHours = `${sign}${minutesToHours(minutesBetween)}`
                } else if (Math.abs(difference) === 0) {
                    subtext = `Jornada completada totalmente`
                    sign = '='
                }

                if (record.isLateCheckin == true) {
                    color = 'red'
                } else if (record.isLateCheckin == false) {
                    color = 'green'
                }
            } else {
                //Tiene check-out pero no tiene turno asignado
                subtext = "Sin turno asignado"
                color = 'gray'
            }

            if (isCanceledByZeroMinutes) {
                subtext = "Conciliación cancelada"
                color = 'gray'
            }
        } else {
            // No tiene check-out
            analisisText = "No se registró salida aún"

            if (record.hasShift) {
                // No tiene check-out, pero tiene turno asignado

                if (record.isLateCheckin === true) {
                    color = 'red'
                    subtext = 'Llegada tarde'
                } else if (record.isLateCheckin === false) {
                    color = 'green'
                    subtext = 'Llegada en tiempo'
                }
            } else {
                // No tiene check-out, ni turno asignado
                subtext = "Sin información aún"
            }
        }


        return {
            fecha: formattedDate,
            ingreso: ingreso,
            egreso: egreso,
            checkinAuditInfo: manualCheckIn,
            checkoutAuditInfo: manualCheckOut,
            // Fichadas de kiosco: el flag viene precalculado en el nodo de la tabla y el
            // recordID es el del registro en sí, no la clave del nodo (que es el ticket).
            checkinRecordID: record.checkinRecordID,
            checkOutRecordID: record.checkOutRecordID,
            isCheckinFromKiosk: record.isCheckinFromKiosk || false,
            isCheckoutFromKiosk: record.isCheckoutFromKiosk || false,
            isCheckinSpoofSuspected: record.isCheckinSpoofSuspected || false,
            isCheckoutSpoofSuspected: record.isCheckoutSpoofSuspected || false,
            analisis: {
                hsTrabajadasText: analisisText,
                subtext: subtext,
                color: color,
                sign: sign,
                xlsExtraHours: xlsExtraHours,
                xlsTotalHours: xlsTotalHours,
                isCheckoutDayOverriden: record.isCheckoutDayOverriden || false,
            },
            abConciliationInfo: record.abConcliationInfo || record.abConciliationInfo || null,
            isCanceled: isCanceledByZeroMinutes,
        }

    }


    //Handlers
    const handleClickDayPicker = () => {
        setAnchorEl(datePickerAnchorRef.current);
        setOpen((previousOpen) => !previousOpen);
    };

    const handleManualEntrada = async () => {
        showPopup(() => 
        <FichadaManualModal 
            orgID={org.id}
            timezone={orgTimezone}
            onSaved={refreshPresenteeism}
        />, false)
    }

    const handleCloseConciliationDetail = () => {
        setConciliationDetailRequest(null);
    };

    const handleConciliationDetailActionCompleted = () => {
        setConciliationDetailRequest(null);
        refreshPresenteeism();
    };

    const handleClosePendingCheckout = () => {
        setPendingCheckoutRequest(null);
    };

    const handlePendingCheckoutSaved = () => {
        setPendingCheckoutRequest(null);
        ToastManager.shared().showSuccessMessage("Salida registrada correctamente".localized());
        refreshPresenteeism();
    };

    const filteredRows = useMemo(() => {
        return response
            .filter(userRow => userRow.colaborador.toLowerCase().replace(/\s/g, '').includes(searchField.toLowerCase().replace(/\s/g, '')))
            .filter(userRow => buildingsToFilter.length === 0 ? true : buildingsToFilter.includes(userRow.edificioID))
            .filter(userRow => analisisFilter.id === 'all' ? true : userRow.analisis.color === analisisFilter.id)
            .filter(userRow => egresosFilter.id === 'all' ? true : (egresosFilter.id === 'pending' ? userRow.egreso === 'Pendiente' : true));
    }, [analisisFilter.id, buildingsToFilter, egresosFilter.id, response, searchField]);

    const emptyStateText = response.length === 0
        ? "No hay registros de presentismo para mostrar.".localized()
        : "No hay registros que coincidan con los filtros.".localized();

    const generateXLSX = async () => {
        let hasUserFilter = searchField.toLowerCase().replace(/\s/g, '').length > 0
        if (dateRange) {
            AnalyticsHelper.trackFichadasRangeExport(
                dateRange[0]?.toLocaleDateString('en-ZA', {timeZone: orgTimezone})?.replace(/\//g, '-'),
                dateRange[1]?.toLocaleDateString('en-ZA', {timeZone: orgTimezone})?.replace(/\//g, '-'),
                hasUserFilter
            )           
        } else {
            AnalyticsHelper.trackFichadasSingleDateExport(
                date.toLocaleDateString('en-ZA', {timeZone: orgTimezone})?.replace(/\//g, '-'),
                hasUserFilter
            )           
        }
        const getColorText = (color) => {
            switch (color) {
                case 'green':
                    return 'INGRESO A TIEMPO';
                case 'red':
                    return 'INGRESO TARDE';
                case 'GRAY':
                    return 'SIN TURNO ASIGNADO';
                default:
                    return 'SIN INFORMACIÓN';
            }
        };
    
        const usersService = Application.shared().coreDependencies.usersService;
        const colaboradores = await usersService.getNomina(org.id)

        let hasLegajo = false;
        if (colaboradores.length !== 0) {
            for (let colaborador of colaboradores) {
                if (colaborador.legajo) {
                    console.log('Tiene legajo', colaborador, colaborador.legajo)
                    hasLegajo = true;
                    break;
                }
            }
        }

        let hasSupervisor = false
        if (org.id === "GSP-FEMSA-COCACOLA-PA" || org.id === "GSP-HEINEKEN-PA") {
            hasSupervisor = true
        }

        const formattedData = response
            .filter(userRow => {return userRow.colaborador.toLowerCase().replace(/\s/g, '').includes(searchField.toLowerCase().replace(/\s/g, ''))})
            .filter(userRow => {return buildingsToFilter.length === 0 ? true : buildingsToFilter.includes(userRow.edificioID)})
            .map(row => {
                const addOneDay = (dateString) => {
                    const [day, month, year] = dateString.split('/');
                    const date = new Date(year, month - 1, day);
                    date.setDate(date.getDate() + 1);
                    return date.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit', year: 'numeric'});
                };

                return {
                    numeroWhatsapp: row.numeroWhatsapp,
                    colaborador: row.colaborador,
                    edificio: row.edificio,
                    ...(hasSupervisor && {
                        supervisor: colaboradores.find(c => c.mobilePhone === row.numeroWhatsapp)?.empresa || "Sin supervisor"
                    }),
                    ...(hasLegajo && { 
                      legajo: colaboradores.find(c => c.mobilePhone === row.numeroWhatsapp)?.legajo || 'Sin legajo' 
                    }),
                    fecha: row.fecha,
                    fechaSalida: row.analisis.isCheckoutDayOverriden ? addOneDay(row.fecha) : row.fecha,
                    ingreso: row.ingreso,
                    egreso: row.egreso,
                    horasTrabajadas: row.analisis.xlsTotalHours,
                    horasExtra: row.analisis.xlsExtraHours,
                    estado: getColorText(row.analisis.color),
                };
            });
        
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        // **Apply the format to the time columns**
        const abcdary = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
        const amountOfColumns = formattedData.length > 0 ? Object.keys(formattedData[0]).length : 0;
        // Format ingreso, egreso, horasTrabajadas, horasExtra columns (last 4 columns)
        const timeColumns = [abcdary[amountOfColumns - 4], abcdary[amountOfColumns - 3], abcdary[amountOfColumns - 2], abcdary[amountOfColumns - 1]];
        timeColumns.forEach(col => {
            for (let row = 2; row <= formattedData.length + 1; row++) {
                const cellAddress = `${col}${row}`;
                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].z = 'h:mm';
                }
            }
        });
      

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      
        const xlsBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([xlsBuffer], { type: 'application/octet-stream' });
      
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        let dateString = date ? date.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit', year: 'numeric'}) : 'rango'
        link.setAttribute('download', `listado_${dateString}.xlsx`);
        link.style.visibility = 'hidden';
      
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };



    return (
    <>
    <div className={styles['content-block']}>
        <div className={styles['header-container']}>
            <div className={styles['title-container']}>
                <h2 className={styles['title']}>
                    Listado
                    <p className={styles['sub-title']}>Presentismo de todos los colaboradores</p>
                </h2>
            </div>

        </div>

        <HorizontalContainer
            className={styles['table-header']}
            width="calc(100% - (var(--margin-lg) * 2))"
            margin="0 var(--margin-lg) var(--margin-md)"
            justifyContent="space-between"
            gap={designSystemStyles.gapMd}
        >
            <HorizontalContainer
                className={styles['table-toolbar-left']}
                width="auto"
                gap={designSystemStyles.gapMd}
            >
                <div className={styles['search-bar-wrapper']}>
                    <SearchBar
                        placeholder={"Buscar...".localized()}
                        value={searchField}
                        onChange={setSearchField}
                    />
                </div>
                <RefreshButton
                    className={styles['refresh-button']}
                    onClick={refreshPresenteeism}
                    text=""
                    width="50px"
                    height="50px"
                />
            </HorizontalContainer>
            <HorizontalContainer
                className={styles['table-toolbar-right']}
                width="auto"
                justifyContent={designSystemStyles.justifyContentRight}
                gap={designSystemStyles.gapMd}
            >
                <div ref={datePickerAnchorRef} className={styles['date-trigger-wrapper']}>
                    <DateTriggerButton
                        className={styles['toolbar-button']}
                        onClick={handleClickDayPicker}
                        text={"Elegir Fecha".localized()}
                        width="168px"
                    />
                    {open && <DatePickerPopperWithRange 
                        inputType={inputDateMethod}
                        updateDate={updateDate} 
                        updateDateRange={updateDateRange}
                        onClickAway={() => {setOpen(false)}}
                        setInputType={setInputDateMethod} 
                        placement="bottom-end"
                        anchorElement={anchorEl} 
                        isOpen={open} 
                        currentPickedDate={date}
                        currentPickedDateRange={dateRange}
                        />
                    }
                </div>

                {canManualRecord &&
                    <HollowedButton
                        className={styles['toolbar-button']}
                        onClick={handleManualEntrada}
                        text={"Fichada manual".localized()}
                        width="180px"
                    />
                }


                <HollowedButton
                    className={styles['toolbar-button']}
                    onClick={generateXLSX}
                    text={"Exportar datos".localized()}
                    width="180px"
                />
            </HorizontalContainer>

        </HorizontalContainer>

        <div className={styles['content-container']}>
            <div className={styles['table-container']} style={{zIndex: 0}}>
                {loading ? (
                    <QontyLoading
                        text={"Cargando listado de presentismo...".localized()}
                        withQonty={true}
                    />
                ) : tableError ? (
                    <QontyEmptyState
                        text={tableError}
                        darkMode={false}
                    />
                ) : filteredRows.length === 0 ? (
                    <QontyEmptyState
                        text={emptyStateText}
                        darkMode={false}
                    />
                ) : (
                    <Table
                        loading={false}
                        dataForRows={filteredRows}
                        columns={columns}
                        rowTemplate={makeRowTemplate(showPopup, hidePopup, orgTimezone, setConciliationDetailRequest, setPendingCheckoutRequest, canManualRecord)}
                    />
                )}
            </div>
        </div>

        <div className={styles['footer-container']}>

        </div>

    </div>
    <PresenteeismConciliationDetailModal
        request={conciliationDetailRequest}
        isOpen={Boolean(conciliationDetailRequest)}
        onClose={handleCloseConciliationDetail}
        onActionCompleted={handleConciliationDetailActionCompleted}
        service={conciliationDetailService}
        timezone={orgTimezone}
    />
    <PresenteeismManualCheckoutModal
        request={pendingCheckoutRequest}
        isOpen={Boolean(pendingCheckoutRequest)}
        onClose={handleClosePendingCheckout}
        onSaved={handlePendingCheckoutSaved}
        service={conciliationDetailService}
        dashboardService={dashboardService}
        timezone={orgTimezone}
    />
    </>
    );
};

const PresenteeismTableControlBoardWrapper = ({org}) => {
    if (!org.flags.hasPresenteeismTable) {
        return <Navigate to="/" />;
    }

    return <PresenteeismTableControlBoard org={org}/>
}

export default PresenteeismTableControlBoardWrapper
